import type { EstimateAssumptions, ScheduleConfig } from '../lib/types';
import { NumberField, TextField } from './Field';

interface Props {
  schedule: ScheduleConfig;
  assumptions: EstimateAssumptions;
  onScheduleChange: (next: ScheduleConfig) => void;
  onAssumptionsChange: (next: EstimateAssumptions) => void;
  invalidPaths: Set<string>;
}

export default function SchedulePanel({
  schedule,
  assumptions,
  onScheduleChange,
  onAssumptionsChange,
  invalidPaths,
}: Props) {
  const patch = (delta: Partial<ScheduleConfig>) => onScheduleChange({ ...schedule, ...delta });

  return (
    <section className="panel" aria-labelledby="schedule-heading">
      <header>
        <h2 id="schedule-heading">Schedule &amp; assumptions</h2>
      </header>
      <div className="panel-body">
        <div className="grid-2">
          <TextField
            label="every"
            hint="blank = tier window"
            value={schedule.every}
            invalid={invalidPaths.has('schedule.every')}
            onChange={(every) => patch({ every })}
            testId="schedule-every"
          />
          <TextField
            label="offset"
            hint="wait for late writes"
            value={schedule.offset}
            invalid={invalidPaths.has('schedule.offset')}
            onChange={(offset) => patch({ offset })}
            testId="schedule-offset"
          />
        </div>
        <TextField
          label="cron (optional)"
          hint="Set to schedule on wall-clock boundaries instead of every. Replaces every in the task option."
          value={schedule.cron}
          placeholder="0 * * * *"
          onChange={(cron) => patch({ cron })}
          testId="schedule-cron"
        />
        <TextField
          label="Late-data tolerance"
          hint="How late a write may arrive and still be picked up. Widens the query range; the extra windows are simply rewritten."
          value={schedule.lateTolerance}
          invalid={invalidPaths.has('schedule.lateTolerance')}
          onChange={(lateTolerance) => patch({ lateTolerance })}
          testId="schedule-late"
        />

        <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '14px 0' }} />

        <div className="grid-2">
          <NumberField
            label="Bytes per point"
            value={assumptions.bytesPerPoint}
            min={0.1}
            step={0.5}
            onChange={(bytesPerPoint) => onAssumptionsChange({ ...assumptions, bytesPerPoint })}
            testId="bytes-per-point"
          />
          <NumberField
            label="Horizon (days)"
            value={assumptions.horizonDays}
            min={1}
            step={30}
            onChange={(horizonDays) => onAssumptionsChange({ ...assumptions, horizonDays })}
            testId="horizon-days"
          />
        </div>
        <p className="hint">
          Bytes per point is the single biggest assumption in the estimate. After TSM compression a
          slow-moving counter can land under 2 bytes while a jittery float costs 16 or more —
          measure your own buckets and put the real number here. The horizon prices infinite
          retention and sets the x-axis of the growth chart.
        </p>
      </div>
    </section>
  );
}
